import type { AreaKind } from './areaCatalog';
import type { V2Evidence } from './evidence';
import type { V2Area, V2ChecklistItem } from './inspection';

// 引导拍照清单 (Owner decision list 2026-10-19 item 31): for each kind of area, the photos a complete record
// usually has, each tied to the checklist row it documents. GUIDANCE ONLY — the coverage checklist stays
// the record of what was inspected (MUST INSPECT ≠ MUST PHOTOGRAPH): nothing here blocks completing an
// area or generating a report. The inspector answers each shot by taking it, or by saying it does not
// apply. For a COMPONENT shot (a handrail, a smoke alarm, a GFCI outlet…) "not here" is an observed
// absence and reaches the AI as the inspector's own fact; the AI judges it against that row's expected
// components (supabase/functions/ai-v2/core/prompt.ts), which raises the recall of missing-item defects.
export interface PhotoShot {
  key: string;
  templateId: string | null;
  zh: string;
  en: string;
  component: boolean;
}

const view = (key: string, templateId: string | null, zh: string, en: string): PhotoShot => ({ key, templateId, zh, en, component: false });
const part = (key: string, templateId: string | null, zh: string, en: string): PhotoShot => ({ key, templateId, zh, en, component: true });

export const PHOTO_GUIDE: Record<AreaKind, readonly PhotoShot[]> = {
  exterior: [
    view('front', 'ext-wall-covering', '正面全景', 'Front elevation'),
    view('rear', 'ext-wall-covering', '背面全景', 'Rear elevation'),
    view('sides', 'ext-wall-covering', '两侧墙面', 'Side elevations'),
    view('foundation', 'ext-foundation-visible', '墙根与可见地基', 'Foundation at grade'),
    view('grading', 'ext-grading', '房屋四周地面坡度', 'Grading at the foundation'),
    part('downspouts', 'ext-gutters', '落水管出口', 'Downspout discharge'),
    part('stairRail', 'ext-entry-steps', '入口台阶扶手', 'Entry stair handrail'),
    part('gfci', 'ext-gfci', '室外插座（GFCI）', 'Exterior GFCI outlet'),
    view('hoseBibb', 'ext-hose-bibb', '室外水龙头', 'Hose bibb')
  ],
  roof: [
    view('slopes', 'roof-covering', '各个坡面', 'Each roof slope'),
    view('flashing', 'roof-flashing', '烟囱与穿屋面处泛水', 'Flashing at chimney and penetrations'),
    part('dripEdge', 'roof-valleys-edges', '屋檐滴水板', 'Drip edge at the eaves'),
    part('gutters', 'roof-gutter-attachment', '天沟', 'Gutters')
  ],
  garage: [
    view('overview', 'garage-structure', '车库全景', 'Garage overview'),
    part('photoEyes', 'garage-door-reverse', '开门机光电眼', 'Opener photo-eye sensors'),
    view('houseDoor', 'garage-separation', '通往住宅的门', 'Door to the house'),
    view('floor', 'garage-floor', '车库地面', 'Garage floor'),
    part('gfci', 'garage-gfci', '车库插座（GFCI）', 'Garage GFCI outlet')
  ],
  basement: [
    view('overview', 'bsmt-access-finish', '地下室全景', 'Basement overview'),
    view('walls', 'bsmt-foundation-walls', '地基墙', 'Foundation walls'),
    view('moisture', 'bsmt-moisture', '墙脚与地面潮气', 'Wall base and floor (moisture)'),
    view('posts', 'bsmt-structure-alignment', '梁与支撑柱', 'Beams and posts'),
    part('stairRail', 'bsmt-stairs', '楼梯扶手', 'Stair handrail'),
    part('alarm', 'bsmt-alarm', '本层烟感', 'Smoke alarm on this level')
  ],
  structure: [
    view('beam', 'str-main-beam', '主梁', 'Main beam'),
    view('postBase', 'str-post-base', '支撑柱底部', 'Post bases'),
    view('joists', 'str-joists', '楼板梁', 'Floor joists')
  ],
  electrical: [
    view('meter', 'elec-service-entrance', '电表与进户线', 'Meter and service entrance'),
    view('panelFront', 'elec-panel-overview', '配电箱外观与前方空间', 'Panel and its working space'),
    view('panelInterior', 'elec-panel-interior', '拆盖后内部', 'Panel interior, cover off'),
    view('panelLabel', 'elec-panel-label', '品牌 / 容量标签', 'Panel label'),
    part('grounding', 'elec-grounding', '接地导线', 'Grounding electrode conductor')
  ],
  plumbing: [
    view('mainShutoff', 'plb-main-shutoff', '主进水与总阀', 'Main water shutoff'),
    view('supply', 'plb-supply-piping', '可见供水管', 'Visible supply piping'),
    view('drain', 'plb-drain-piping', '可见排水管', 'Visible drain piping'),
    view('cleanout', 'plb-sewer-cleanout', '主排污清理口', 'Main sewer cleanout')
  ],
  waterHeater: [
    view('overview', 'wh-overview', '热水器整体', 'Water heater overview'),
    view('dataPlate', 'wh-data-plate', '铭牌', 'Data plate'),
    part('tprPipe', 'wh-tpr', 'TPR 泄压阀排放管', 'TPR valve discharge pipe'),
    view('venting', 'wh-venting', '排气 / 烟道', 'Venting'),
    view('base', 'wh-base', '底部与接水盘', 'Base and drain pan')
  ],
  hvac: [
    view('unit', 'hvac-heating-unit', '供暖设备整体', 'Heating unit'),
    view('dataPlate', 'hvac-heating-unit', '供暖设备铭牌', 'Heating unit data plate'),
    view('venting', 'hvac-venting', '排气 / 烟道', 'Venting'),
    part('filter', 'hvac-filter', '过滤器', 'Air filter'),
    view('condenser', 'hvac-ac-unit', '空调室外机', 'AC condenser')
  ],
  attic: [
    view('access', 'attic-access', '入口与可观察范围', 'Access and view'),
    view('sheathing', 'attic-sheathing', '屋面板下侧', 'Roof sheathing underside'),
    view('insulation', 'attic-insulation', '保温层', 'Insulation'),
    part('ventilation', 'attic-ventilation', '进风与出风口', 'Intake and exhaust vents'),
    view('exhaust', 'attic-exhaust-termination', '排气管终点', 'Exhaust duct terminations')
  ],
  interior: [
    view('overview', 'int-surfaces', '房间全景', 'Room overview'),
    view('windows', 'int-windows', '门窗', 'Windows and doors'),
    part('stairRail', 'int-stairs', '楼梯扶手', 'Stair handrail'),
    part('alarm', 'int-alarms', '本层烟感 / CO 报警器', 'Smoke / CO alarm on this level')
  ],
  kitchen: [
    view('overview', 'kit-surfaces', '厨房全景', 'Kitchen overview'),
    view('sink', 'kit-sink', '水槽与龙头', 'Sink and faucet'),
    view('underSink', 'kit-sink', '水槽下柜', 'Under the sink'),
    part('gfci', 'kit-gfci', '台面插座（GFCI）', 'Countertop GFCI outlet'),
    view('range', 'kit-gas', '灶具与燃气接口', 'Range and gas connection'),
    view('exhaust', 'kit-exhaust-ducting', '排风 / 抽油烟机', 'Exhaust / range hood')
  ],
  bathroom: [
    view('overview', 'bath-surfaces', '卫生间全景', 'Bathroom overview'),
    view('tubShower', 'bath-tub-shower', '浴缸 / 淋浴', 'Tub / shower'),
    view('toilet', 'bath-toilet', '马桶底部', 'Toilet base'),
    view('underSink', 'bath-sink', '洗手盆下柜', 'Under the sink'),
    part('exhaust', 'bath-exhaust', '排风扇或可开的窗', 'Exhaust fan or opening window'),
    part('gfci', 'bath-gfci', '漏电保护插座（GFCI）', 'GFCI outlet')
  ],
  bedroom: [
    view('overview', 'bed-surfaces', '卧室全景', 'Bedroom overview'),
    view('window', 'bed-egress', '窗户（试开）', 'Window (opened)'),
    part('alarm', 'bed-alarm', '卧室内烟感', 'Smoke alarm in the bedroom'),
    view('outlets', 'bed-outlets', '插座', 'Outlets')
  ],
  safety: [
    part('alarms', 'safe-alarms-system', '各层烟感 / CO 报警器', 'Smoke / CO alarms on each level'),
    part('handrails', 'safe-handrails', '楼梯扶手与护栏', 'Stair handrails and guards')
  ],
  other: [view('overview', 'other-condition', '区域全景', 'Area overview')]
};

export function photoShotsFor(kind: AreaKind): readonly PhotoShot[] {
  return PHOTO_GUIDE[kind] ?? [];
}

export function photoShotOf(kind: AreaKind, key: string | null | undefined): PhotoShot | null {
  if (!key) return null;
  return photoShotsFor(kind).find((s) => s.key === key) ?? null;
}

export type ShotState = 'taken' | 'skipped' | 'open';

export interface ShotStatus {
  shot: PhotoShot;
  state: ShotState;
  photoCount: number;
}

// Each shot of the area: taken (a photo of the area carries it), skipped (the inspector said it does not
// apply / is not here) or open. A shot with a photo counts as taken even if it was skipped earlier.
// 2026-10-27 (device walkthrough: 42 imported photos sorted by the AI, 「拍照清单 · 已完成 0 / 9」): a shot
// that documents a checklist row also counts as taken when that row carries any linked photo — a pool
// import the AI or the inspector put on the row — so the list is not answered twice. The caller passes the
// template ids of the area's rows that hold photos (`linkedTemplateIds`).
export function photoGuideStatus(
  area: Pick<V2Area, 'id' | 'kind' | 'shotSkips'>,
  evidence: readonly Pick<V2Evidence, 'areaId' | 'shot'>[],
  linkedTemplateIds: ReadonlySet<string> = new Set()
): { shots: ShotStatus[]; answered: number; total: number } {
  const skips = new Set(area.shotSkips ?? []);
  const shots = photoShotsFor(area.kind).map((shot) => {
    const photoCount = evidence.filter((e) => e.areaId === area.id && e.shot === shot.key).length;
    const linked = shot.templateId !== null && shot.templateId !== undefined && linkedTemplateIds.has(shot.templateId);
    const state: ShotState = photoCount > 0 || linked ? 'taken' : skips.has(shot.key) ? 'skipped' : 'open';
    return { shot, state, photoCount };
  });
  return { shots, answered: shots.filter((s) => s.state !== 'open').length, total: shots.length };
}

// The template ids of an area's rows that carry at least one linked photo (for photoGuideStatus).
export function linkedTemplateIdsOf(items: readonly Pick<V2ChecklistItem, 'areaId' | 'templateId' | 'evidenceIds'>[], areaId: string): Set<string> {
  const out = new Set<string>();
  for (const item of items) if (item.areaId === areaId && item.templateId && item.evidenceIds.length > 0) out.add(item.templateId);
  return out;
}

// What the AI is told (supabase/functions/_shared/aiV2Protocol.ts AiV2PhotoGuide): the components the
// inspector said are NOT here (with the checklist row they belong to), and the views nobody answered.
export function photoGuideForAi(
  area: Pick<V2Area, 'id' | 'kind' | 'shotSkips'>,
  evidence: readonly Pick<V2Evidence, 'areaId' | 'shot'>[],
  itemIdOfTemplate: (templateId: string) => string | null,
  linkedTemplateIds: ReadonlySet<string> = new Set()
): { absent: { itemId: string | null; name: string }[]; notTaken: string[] } | null {
  const status = photoGuideStatus(area, evidence, linkedTemplateIds);
  if (status.total === 0) return null;
  const absent = status.shots.filter((s) => s.state === 'skipped' && s.shot.component).map((s) => ({ itemId: s.shot.templateId ? itemIdOfTemplate(s.shot.templateId) : null, name: s.shot.en }));
  const notTaken = status.shots.filter((s) => s.state === 'open').map((s) => s.shot.en);
  return absent.length === 0 && notTaken.length === 0 ? null : { absent, notTaken };
}
